import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/*
 * Opening a record (Project, Opportunity, Quick Win) as its own page. The page
 * it was opened from is named in the back link. `replace` is for a record
 * opened from a ?open= link, so Back does not land on that link and open the
 * record again.
 */

const BACK_LABELS: [string, string][] = [
  ['/projects', 'Back to Projects'],
  ['/dashboard', 'Back to Home'],
  ['/portfolio', 'Back to Portfolio'],
  ['/results', 'Back to Results'],
  ['/my-work', 'Back to My work'],
];

const backLabelFor = (pathname: string): string => {
  const hit = BACK_LABELS.find(([path]) => pathname === path || pathname.startsWith(`${path}/`));
  if (hit) return hit[1];
  if (pathname === '/') return 'Back to Home';
  return 'Back';
};

export const projectPath = (id: string) => `/projects/${encodeURIComponent(id)}`;
export const opportunityPath = (id: string) => `/projects/opportunities/${encodeURIComponent(id)}`;
export const quickWinPath = (id: string) => `/projects/quick-wins/${encodeURIComponent(id)}`;

export const useRecordLinks = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return useMemo(() => {
    const go = (path: string, replace = false) => navigate(path, { replace, state: { backLabel: backLabelFor(pathname) } });
    return {
      project: (id: string | null | undefined, replace = false) => { if (id) go(projectPath(id), replace); },
      opportunity: (id: string | null | undefined, replace = false) => { if (id) go(opportunityPath(id), replace); },
      quickWin: (id: string | null | undefined, replace = false) => { if (id) go(quickWinPath(id), replace); },
    };
  }, [navigate, pathname]);
};
